import React, { useEffect, useMemo, useState, createElement, Component } from 'react';
import { Download, Package, XCircle, Check, Loader2, Plus, RefreshCw // Nuevo icono para indicar carga
} from 'lucide-react';
import { MetricsBar } from '../components/MetricsBar';
import { FilterDropdown } from '../components/ui/FilterDropdown';
import { SearchBar } from '../components/ui/SearchBar';
import { Button } from '../components/ui/Button';
import { Pagination } from '../components/ui/Pagination';
import { useToast } from '../components/ui/Toast';
import { useOrders } from '../contexts/OrderContext';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { getCompanies } from '../utils/companyManagement';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { filterTodayOrders } from '../utils/dateFilters';
import { cleanProductName } from '../utils/formatters';
export function OrdersDashboard() {
  const {
    filteredOrders,
    orders,
    loading,
    cancelOrder,
    updateOrderStatus,
    addOrder,
    refreshOrders // <--- IMPORTANTE: Obtenemos la función de recarga
  } = useOrders();
  const {
    addLog
  } = useActivityLog();
  const {
    showToast
  } = useToast();

  // Estados
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState('Todas');
  const [branchFilter, setBranchFilter] = useState('Todas');
  const [dateFilter, setDateFilter] = useState<'today' | 'all'>('today');
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false); // Estado visual de carga

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const companies = getCompanies();
  const companyNames = ['Todas', ...companies.map(c => c.name)];

  // --- EFECTO DE ACTUALIZACIÓN AUTOMÁTICA (POLLING) ---
  useEffect(() => {
    if (refreshOrders) {
      // Función para ejecutar la recarga
      const handleRefresh = async () => {
        setIsAutoRefreshing(true);
        try {
          await refreshOrders();
        } catch (error) {
          console.error("Error auto-refreshing orders:", error);
        } finally {
          // Pequeño delay para que el icono no parpadee demasiado rápido
          setTimeout(() => setIsAutoRefreshing(false), 500);
        }
      };

      // Ejecutar intervalo cada 5 segundos
      const interval = setInterval(handleRefresh, 5000);

      // Limpiar intervalo al desmontar
      return () => clearInterval(interval);
    }
  }, [refreshOrders]);

  // Sincronización en tiempo real (Supabase Subscription) - Se mantiene como respaldo
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    const subscription = supabaseClient.subscribeToOrders(() => {
      console.log('Order updated via Realtime, reloading...');
      if (refreshOrders) refreshOrders();
    });
    return () => {
      subscription?.unsubscribe();
    };
  }, [refreshOrders]);

  // Load menu items and employees for order creation
  useEffect(() => {
    const loadData = async () => {
      try {
        if (isSupabaseConfigured()) {
          const [items, emps] = await Promise.all([supabaseClient.getMenuItems(), supabaseClient.getEmployees()]);
          setMenuItems(items || []);
          setEmployees(emps || []);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  // Get branches for selected company
  const availableBranches = useMemo(() => {
    if (companyFilter === 'Todas') {
      return ['Todas'];
    }
    const company = companies.find(c => c.name === companyFilter);
    return ['Todas', ...(company?.branches.map(b => b.name) || [])];
  }, [companyFilter, companies]);

  // Reset branch filter when company changes
  useEffect(() => {
    setBranchFilter('Todas');
  }, [companyFilter]);

  // Filter orders by date, company and branch
  const displayOrders = useMemo(() => {
    let filtered = filteredOrders(searchTerm, companyFilter);

    // Filtrar por fecha (solo órdenes de hoy)
    if (dateFilter === 'today') {
      filtered = filterTodayOrders(filtered);
    }

    // Apply branch filter
    if (branchFilter !== 'Todas') {
      filtered = filtered.filter(order => order.branch === branchFilter);
    }
    return filtered;
  }, [filteredOrders, searchTerm, companyFilter, branchFilter, dateFilter]);

  // Paginación
  const totalPages = Math.ceil(displayOrders.length / itemsPerPage);
  const paginatedOrders = displayOrders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Reset a primera página cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, companyFilter, branchFilter, dateFilter]);

  // Calculate totals from DISPLAYED orders only (respecting date filter)
  const activeDisplayOrders = displayOrders.filter(o => o.status !== 'Cancelado');
  const totalSubtotal = activeDisplayOrders.reduce((sum, order) => {
    const total = parseFloat(order.total);
    return sum + (order.taxExempt ? total : total / 1.18);
  }, 0);
  const totalTax = activeDisplayOrders.reduce((sum, order) => {
    const total = parseFloat(order.total);
    return sum + (order.taxExempt ? 0 : total - total / 1.18);
  }, 0);
  const totalAmount = activeDisplayOrders.reduce((sum, order) => sum + parseFloat(order.total), 0);
  const totalDiscount = activeDisplayOrders.reduce((sum, order) => sum + parseFloat(order.discount || '0'), 0);
  const totalSubsidy = activeDisplayOrders.reduce((sum, order) => sum + parseFloat(order.subsidyAmount || '0'), 0);
  const totalCount = activeDisplayOrders.length;
  const metrics = [{
    label: 'Cantidad',
    value: totalCount.toString()
  }, {
    label: 'Subtotal',
    value: `RD$ ${totalSubtotal.toLocaleString('es-DO', {
      minimumFractionDigits: 2
    })}`
  }, {
    label: 'ITBIS (18%)',
    value: `RD$ ${totalTax.toLocaleString('es-DO', {
      minimumFractionDigits: 2
    })}`
  }, {
    label: 'Descuento',
    value: `RD$ ${totalDiscount.toLocaleString('es-DO', {
      minimumFractionDigits: 2
    })}`
  }, {
    label: 'Subsidios',
    value: `RD$ ${totalSubsidy.toLocaleString('es-DO', {
      minimumFractionDigits: 2
    })}`
  }, {
    label: 'Total Facturado',
    value: `RD$ ${totalAmount.toLocaleString('es-DO', {
      minimumFractionDigits: 2
    })}`
  }];
  const handleCancelOrder = (order: any) => {
    setSelectedOrder(order);
    setShowCancelModal(true);
  };
  const handleChangeStatus = (order: any) => {
    setSelectedOrder(order);
    setShowStatusModal(true);
  };
  const confirmCancel = async () => {
    if (selectedOrder) {
      try {
        await cancelOrder(selectedOrder.id);
        addLog('Pedido Anulado', `Orden #${selectedOrder.orderNo} - ${selectedOrder.name} - ${selectedOrder.company}`, 'order');
        showToast('success', 'Pedido anulado', `Orden #${selectedOrder.orderNo} ha sido anulada`);
        setShowCancelModal(false);
        setSelectedOrder(null);
      } catch (error) {
        showToast('error', 'Error', 'No se pudo anular el pedido');
      }
    }
  };
  const confirmStatusChange = async (newStatus: any) => {
    if (selectedOrder) {
      try {
        await updateOrderStatus(selectedOrder.id, newStatus);
        addLog('Estado de Pedido Actualizado', `Orden #${selectedOrder.orderNo} cambió a ${newStatus}`, 'order');
        showToast('success', 'Estado actualizado', `Orden #${selectedOrder.orderNo} ahora está ${newStatus}`);
        setShowStatusModal(false);
        setSelectedOrder(null);
      } catch (error) {
        showToast('error', 'Error', 'No se pudo actualizar el estado');
      }
    }
  };
  const exportOrders = () => {
    const ordersToExport = displayOrders;
    if (ordersToExport.length === 0) {
      showToast('warning', 'Sin datos', 'No hay órdenes para exportar');
      return;
    }
    let csv = 'Órdenes - Cricket\n\n';
    csv += `Fecha de Exportación:,${new Date().toLocaleString('es-DO')}\n`;
    csv += `Filtro de Empresa:,${companyFilter}\n`;
    csv += `Filtro de Sucursal:,${branchFilter}\n`;
    csv += `Filtro de Fecha:,${dateFilter === 'today' ? 'Solo hoy' : 'Todas'}\n`;
    csv += `Total de Órdenes:,${ordersToExport.length}\n\n`;
    csv += 'No. Orden,Fecha,Cliente,Empresa,Sucursal,Producto,Cantidad,Subtotal,ITBIS,Descuento,Subsidio,Total,Estado,Etiqueta,Exento\n';
    ordersToExport.forEach(order => {
      const orderTotal = parseFloat(order.total);
      const orderSubtotal = order.taxExempt ? orderTotal : orderTotal / 1.18;
      const orderTax = order.taxExempt ? 0 : orderTotal - orderTotal / 1.18;
      const orderSubsidy = parseFloat(order.subsidyAmount || '0');
      csv += `${order.orderNo},${order.orderDate},${order.name},${order.company},${order.branch},"${order.product}",${order.quantity},RD$ ${orderSubtotal.toFixed(2)},RD$ ${orderTax.toFixed(2)},RD$ ${order.discount || '0.00'},RD$ ${orderSubsidy.toFixed(2)},RD$ ${order.total},${order.status},${order.labelStatus},${order.taxExempt ? 'Sí' : 'No'}\n`;
    });
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `ordenes-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('success', 'Exportación exitosa', `${ordersToExport.length} órdenes exportadas`);
  };

  // Loading state
  if (loading) {
    return <div className="p-4 lg:p-8 max-w-[1600px] mx-auto" data-id="element-2071">
        <div className="flex items-center justify-center h-96" data-id="element-2072">
          <div className="text-center" data-id="element-2073">
            <Loader2 className="w-12 h-12 animate-spin text-gray-400 mx-auto mb-4" data-id="element-2074" />
            <p className="text-gray-600 dark:text-gray-400" data-id="element-2075">
              Cargando pedidos...
            </p>
          </div>
        </div>
      </div>;
  }
  return <div className="p-4 lg:p-8 max-w-[1600px] mx-auto" data-id="element-2076">
      <MetricsBar metrics={metrics} data-id="element-2077" />

      <div className="bg-white dark:bg-gray-900 rounded-xl p-4 lg:p-6 shadow-sm border border-gray-200 dark:border-gray-800" data-id="element-2078">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6" data-id="element-2079">
          <div data-id="element-2080">
            <div className="flex items-center gap-2" data-id="element-2081">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-2082">
                Órdenes {dateFilter === 'today' ? 'de Hoy' : ''}
              </h2>
              {/* Indicador de actualización automática */}
              {isAutoRefreshing && <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" data-id="element-2083" />}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" data-id="element-2084">
              {displayOrders.length} órdenes encontradas
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto" data-id="element-2085">
            <select value={dateFilter} onChange={e => setDateFilter(e.target.value as 'today' | 'all')} className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2086">
              <option value="today" data-id="element-2087">Solo hoy</option>
              <option value="all" data-id="element-2088">Todas las fechas</option>
            </select>

            <FilterDropdown label="Empresa" value={companyFilter} options={companyNames} onChange={setCompanyFilter} className="w-full sm:w-40" data-id="element-2089" />

            {companyFilter !== 'Todas' && <FilterDropdown label="Sucursal" value={branchFilter} options={availableBranches} onChange={setBranchFilter} className="w-full sm:w-48" data-id="element-2090" />}

            <SearchBar className="w-full sm:w-64" onSearch={setSearchTerm} placeholder="Buscar orden..." data-id="element-2091" />

            <Button variant="outline" leftIcon={<Plus className="w-4 h-4" data-id="element-2093" />} onClick={() => setShowCreateOrderModal(true)} data-id="element-2092">
              Nueva Orden
            </Button>

            <Button variant="primary" leftIcon={<Download className="w-4 h-4" data-id="element-2095" />} onClick={exportOrders} data-id="element-2094">
              Exportar
            </Button>
          </div>
        </div>

        {orders.length === 0 ? <div className="text-center py-20 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700" data-id="element-2096">
            <Package size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" data-id="element-2097" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-2098">
              No hay órdenes registradas
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mt-2" data-id="element-2099">
              Las órdenes aparecerán aquí cuando los empleados hagan pedidos.
            </p>
          </div> : displayOrders.length === 0 ? <div className="text-center py-20 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700" data-id="element-2100">
            <Package size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" data-id="element-2101" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-2102">
              {dateFilter === 'today' ? 'No hay pedidos para hoy' : 'No se encontraron órdenes'}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mt-2" data-id="element-2103">
              {dateFilter === 'today' ? 'Los pedidos de hoy aparecerán aquí cuando se registren.' : 'Intenta ajustar los filtros de búsqueda'}
            </p>
          </div> : <>
            <div className="overflow-x-auto" data-id="element-2104">
              <table className="w-full text-left border-collapse min-w-[1300px]" data-id="element-2105">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700" data-id="element-2106">
                  <tr data-id="element-2107">
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-2108">
                      No. Orden
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-2109">
                      Fecha/Hora
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-2110">
                      Cliente
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-2111">
                      Empresa
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-2112">
                      Producto
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-2113">
                      Cant.
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-2114">
                      Subsidio
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-2115">
                      Total
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-2116">
                      Estado
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-2117">
                      Etiqueta
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase text-right" data-id="element-2118">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900" data-id="element-2119">
                  {paginatedOrders.map(order => <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-id="element-2120">
                      <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white" data-id="element-2121">
                        {order.orderNo}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300" data-id="element-2122">
                        {order.orderDate}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white" data-id="element-2123">
                        {order.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300" data-id="element-2124">
                        {order.company}
                        {order.taxExempt && <span className="ml-2 text-xs text-green-600 dark:text-green-400" data-id="element-2125">
                            (Exento)
                          </span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300" data-id="element-2126">
                        {cleanProductName(order.product)}
                      </td>
                      <td className="px-4 py-3 text-sm text-center text-gray-900 dark:text-white" data-id="element-2127">
                        {order.quantity}
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-2128">
                        {order.subsidyAmount && parseFloat(order.subsidyAmount) > 0 ? <span className="text-green-600 dark:text-green-400 font-medium" data-id="element-2129">
                            RD$ {parseFloat(order.subsidyAmount).toFixed(2)}
                          </span> : <span className="text-gray-400 dark:text-gray-500" data-id="element-2130">
                            -
                          </span>}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white" data-id="element-2131">
                        RD$ {parseFloat(order.total).toFixed(2)}
                      </td>
                      <td className="px-4 py-3" data-id="element-2132">
                        <button onClick={() => handleChangeStatus(order)} disabled={order.status === 'Cancelado'} className={`px-2 py-1 text-xs font-medium rounded-full ${order.status === 'Pendiente' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' : order.status === 'En Preparación' ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' : order.status === 'Listo' ? 'bg-green-100 text-green-800 hover:bg-green-200' : order.status === 'Entregado' ? 'bg-gray-100 text-gray-800' : 'bg-red-100 text-red-800'} ${order.status !== 'Cancelado' && order.status !== 'Entregado' ? 'cursor-pointer' : 'cursor-default'}`} data-id="element-2133">
                          {order.status}
                        </button>
                      </td>
                      <td className="px-4 py-3" data-id="element-2134">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${order.labelStatus === 'Generado' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`} data-id="element-2135">
                          {order.labelStatus}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-2136">
                        {order.status !== 'Cancelado' && order.status !== 'Entregado' && <button onClick={() => handleCancelOrder(order)} className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium" data-id="element-2137">
                              Anular
                            </button>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={displayOrders.length} itemsPerPage={itemsPerPage} data-id="element-2138" />}
          </>}
      </div>

      {/* Create Order Modal */}
      {showCreateOrderModal && <CreateOrderModal companies={companies} employees={employees} menuItems={menuItems} onClose={() => setShowCreateOrderModal(false)} onSave={(orderData: any) => {
      try {
        addOrder(orderData);
        addLog('Orden Creada por Admin', `${orderData.name} - ${orderData.company}`, 'order');
        showToast('success', 'Orden creada', 'La orden ha sido creada exitosamente');
        setShowCreateOrderModal(false);
      } catch (error) {
        showToast('error', 'Error', 'No se pudo crear la orden');
      }
    }} data-id="element-2139" />}

      {/* Status Change Modal */}
      {showStatusModal && selectedOrder && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-2140">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-2141">
            <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-2142">
              <div className="flex items-center gap-3" data-id="element-2143">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg" data-id="element-2144">
                  <Check className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-2145" />
                </div>
                <div data-id="element-2146">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-2147">
                    Cambiar Estado
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2148">
                    Orden #{selectedOrder.orderNo}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6" data-id="element-2149">
              <p className="text-gray-700 dark:text-gray-300 mb-4" data-id="element-2150">
                Selecciona el nuevo estado del pedido:
              </p>
              <div className="space-y-2" data-id="element-2151">
                {['Pendiente', 'En Preparación', 'Listo', 'Entregado'].map(status => <button key={status} onClick={() => confirmStatusChange(status)} disabled={selectedOrder.status === status} className={`w-full p-3 rounded-lg text-left font-medium transition-colors ${selectedOrder.status === status ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed' : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white'}`} data-id="element-2152">
                      {status}
                    </button>)}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-800" data-id="element-2153">
              <Button variant="outline" onClick={() => setShowStatusModal(false)} fullWidth data-id="element-2154">
                Cancelar
              </Button>
            </div>
          </div>
        </div>}

      {/* Cancel Order Modal */}
      {showCancelModal && selectedOrder && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-2155">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-2156">
            <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-2157">
              <div className="flex items-center gap-3" data-id="element-2158">
                <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg" data-id="element-2159">
                  <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" data-id="element-2160" />
                </div>
                <div data-id="element-2161">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-2162">
                    Anular Pedido
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2163">
                    Orden #{selectedOrder.orderNo}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6" data-id="element-2164">
              <p className="text-gray-700 dark:text-gray-300 mb-4" data-id="element-2165">
                ¿Estás seguro que deseas anular este pedido? Esta acción no se
                puede deshacer.
              </p>
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg" data-id="element-2166">
                <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-2167">
                  <strong data-id="element-2168">Cliente:</strong> {selectedOrder.name}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-2169">
                  <strong data-id="element-2170">Producto:</strong> {selectedOrder.product}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-2171">
                  <strong data-id="element-2172">Total:</strong> RD$ {selectedOrder.total}
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex gap-3" data-id="element-2173">
              <Button variant="outline" onClick={() => setShowCancelModal(false)} fullWidth data-id="element-2174">
                Cancelar
              </Button>
              <Button onClick={confirmCancel} fullWidth className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800" data-id="element-2175">
                Anular Pedido
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}

// Create Order Modal Component
function CreateOrderModal({
  companies,
  employees,
  menuItems,
  onClose,
  onSave
}: any) {
  const [formData, setFormData] = useState({
    employeeId: '',
    companyId: 0,
    branchId: '',
    menuItemId: '',
    quantity: 1,
    type: 'Envío'
  });
  const selectedCompany = companies.find((c: any) => c.id === formData.companyId);
  const selectedEmployee = employees.find((e: any) => e.id === formData.employeeId);
  const selectedMenuItem = menuItems.find((m: any) => m.id === formData.menuItemId);
  const selectedBranch = selectedCompany?.branches.find((b: any) => b.id === formData.branchId);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmployee || !selectedCompany || !selectedBranch || !selectedMenuItem) {
      return;
    }
    const subtotal = selectedMenuItem.price * formData.quantity;
    const tax = selectedCompany.taxExempt ? 0 : subtotal * 0.18;
    const total = subtotal + tax;
    const orderData = {
      deliveryDate: new Date().toLocaleString('es-DO'),
      type: formData.type,
      name: selectedEmployee.name,
      company: selectedCompany.name,
      branch: selectedBranch.name,
      product: selectedMenuItem.name,
      cashier: 'Admin',
      quantity: formData.quantity,
      catalogType: selectedMenuItem.catalogType || 'Almuerzo',
      subtotal: subtotal.toFixed(2),
      discount: '0',
      total: total.toFixed(2),
      taxExempt: selectedCompany.taxExempt,
      employeeId: selectedEmployee.id
    };
    onSave(orderData);
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-2176">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-2xl border border-gray-200 dark:border-gray-800 max-h-[90vh] overflow-y-auto" data-id="element-2177">
        <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-2178">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-2179">
            Crear Nueva Orden
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-id="element-2180">
          <div data-id="element-2181">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2182">
              Empleado
            </label>
            <select required value={formData.employeeId} onChange={e => {
            const emp = employees.find((emp: any) => emp.id === e.target.value);
            setFormData({
              ...formData,
              employeeId: e.target.value,
              companyId: emp?.company_id || 0,
              branchId: emp?.branch_id || ''
            });
          }} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2183">
              <option value="" data-id="element-2184">Seleccionar empleado</option>
              {employees.map((emp: any) => <option key={emp.id} value={emp.id} data-id="element-2185">
                  {emp.name} -{' '}
                  {companies.find((c: any) => c.id === emp.company_id)?.name}
                </option>)}
            </select>
          </div>

          {selectedEmployee && <>
              <div className="grid grid-cols-2 gap-4" data-id="element-2186">
                <div data-id="element-2187">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2188">
                    Empresa
                  </label>
                  <input type="text" disabled value={selectedCompany?.name || ''} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2189" />
                </div>

                <div data-id="element-2190">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2191">
                    Sucursal
                  </label>
                  <input type="text" disabled value={selectedBranch?.name || ''} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2192" />
                </div>
              </div>

              <div data-id="element-2193">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2194">
                  Plato
                </label>
                <select required value={formData.menuItemId} onChange={e => setFormData({
              ...formData,
              menuItemId: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2195">
                  <option value="" data-id="element-2196">Seleccionar plato</option>
                  {menuItems.map((item: any) => <option key={item.id} value={item.id} data-id="element-2197">
                      {item.name} - RD$ {item.price.toFixed(2)}
                    </option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-2198">
                <div data-id="element-2199">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2200">
                    Cantidad
                  </label>
                  <input type="number" required min="1" value={formData.quantity} onChange={e => setFormData({
                ...formData,
                quantity: parseInt(e.target.value)
              })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2201" />
                </div>

                <div data-id="element-2202">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2203">
                    Tipo
                  </label>
                  <select value={formData.type} onChange={e => setFormData({
                ...formData,
                type: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2204">
                    <option value="Envío" data-id="element-2205">Envío</option>
                    <option value="Retiro" data-id="element-2206">Retiro</option>
                  </select>
                </div>
              </div>

              {selectedMenuItem && <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg" data-id="element-2207">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2" data-id="element-2208">
                    Resumen
                  </h3>
                  <div className="space-y-1 text-sm" data-id="element-2209">
                    <div className="flex justify-between" data-id="element-2210">
                      <span className="text-gray-600 dark:text-gray-300" data-id="element-2211">
                        Subtotal:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-2212">
                        RD${' '}
                        {(selectedMenuItem.price * formData.quantity).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-2213">
                      <span className="text-gray-600 dark:text-gray-300" data-id="element-2214">
                        ITBIS (18%):
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-2215">
                        RD${' '}
                        {selectedCompany?.taxExempt ? '0.00' : (selectedMenuItem.price * formData.quantity * 0.18).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700" data-id="element-2216">
                      <span className="font-semibold text-gray-900 dark:text-white" data-id="element-2217">
                        Total:
                      </span>
                      <span className="font-bold text-gray-900 dark:text-white" data-id="element-2218">
                        RD${' '}
                        {(selectedMenuItem.price * formData.quantity * (selectedCompany?.taxExempt ? 1 : 1.18)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>}
            </>}

          <div className="flex gap-3 pt-4" data-id="element-2219">
            <Button type="button" variant="outline" onClick={onClose} fullWidth data-id="element-2220">
              Cancelar
            </Button>
            <Button type="submit" fullWidth disabled={!selectedEmployee || !selectedMenuItem} data-id="element-2221">
              Crear Orden
            </Button>
          </div>
        </form>
      </div>
    </div>;
}